/**
 * Strictly-necessary storage notice (legal/ui-consent-language.md §8).
 * There is no accept or reject choice because Pacarion does not use analytics cookies.
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { COOKIE_NOTICE, followPolicyLink } from '../utils/legal';
import { isNativeApp } from '../utils/platform';

const SEEN_KEY = 'pacarion_cookie_consent';
const SEEN_DATE_KEY = 'pacarion_cookie_consent_date';

function alreadySeen() {
  const value = localStorage.getItem(SEEN_KEY);
  return value === 'seen' || value === 'true';
}

function nativeShell() {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('native-app')) {
    return true;
  }
  try {
    return isNativeApp();
  } catch (error) {
    return false;
  }
}

const CookieConsent = ({ signedIn } = {}) => {
  const { colors } = useTheme();
  const [isVisible, setIsVisible] = useState(() => !alreadySeen());
  const [detectedSignIn, setDetectedSignIn] = useState(
    () => typeof document !== 'undefined' && document.documentElement.dataset.signedIn === '1',
  );

  useEffect(() => {
    if (signedIn !== undefined) return undefined;
    const sync = () => setDetectedSignIn(document.documentElement.dataset.signedIn === '1');
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-signed-in'] });
    return () => observer.disconnect();
  }, [signedIn]);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, 'seen');
    localStorage.setItem(SEEN_DATE_KEY, new Date().toISOString());
    setIsVisible(false);
  };

  const showForUser = signedIn === undefined ? detectedSignIn : signedIn;
  if (!showForUser || nativeShell() || !isVisible) return null;

  return (
    <div className="cookie-notice" style={styles.overlay}>
      <div style={{ ...styles.banner, background: colors.surface, border: `1px solid ${colors.border}` }}>
        <p style={{ ...styles.description, color: colors.textSecondary }}>
          {COOKIE_NOTICE}{' '}
          <a
            href="/cookies"
            onClick={followPolicyLink}
            style={{ ...styles.link, color: '#59c4c4', borderBottomColor: '#59c4c4' }}
          >
            Cookie Policy
          </a>
        </p>
        <button type="button" onClick={dismiss} style={styles.button}>
          Got it
        </button>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem',
    pointerEvents: 'none',
    boxSizing: 'border-box',
  },
  banner: {
    maxWidth: '640px',
    width: '100%',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    padding: '1.25rem 1.5rem',
    pointerEvents: 'all',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    minWidth: 0,
  },
  description: {
    fontSize: '0.9rem',
    lineHeight: '1.6',
    margin: 0,
    minWidth: 0,
    overflowWrap: 'anywhere',
    fontWeight: 300,
    letterSpacing: '0.01em',
  },
  button: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 400,
    fontSize: '0.95rem',
    letterSpacing: '0.02em',
    whiteSpace: 'normal',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)',
    flexShrink: 0,
  },
  link: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 400,
    borderBottom: '1px solid',
    padding: 0,
    fontSize: 'inherit',
    fontFamily: 'inherit',
  },
};

export default CookieConsent;
